import type {
  AddFollowUpCommentInput,
  AssignSupervisorInput,
  EvaluatorType,
  FollowUpSheetResponse,
  ReturnEvaluationInput,
  SubmitEvaluationInput,
} from '@lms/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

const FOLLOW_UPS_KEY = 'follow-ups'

export function useFollowUpSheet(enrollmentId: string) {
  return useQuery({
    queryKey: [FOLLOW_UPS_KEY, enrollmentId],
    queryFn: () =>
      api.get<FollowUpSheetResponse>(`/api/training-follow-ups/enrollment/${enrollmentId}`),
    enabled: !!enrollmentId,
  })
}

interface EvaluationTarget {
  followUpId: string
  evaluatorType: EvaluatorType
}

const evaluationPath = ({ followUpId, evaluatorType }: EvaluationTarget) =>
  `/api/training-follow-ups/${followUpId}/evaluations/${evaluatorType}`

export function useSubmitEvaluation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ data, ...target }: EvaluationTarget & { data: SubmitEvaluationInput }) =>
      api.post(evaluationPath(target), data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [FOLLOW_UPS_KEY] }),
  })
}

export function useReturnEvaluation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ data, ...target }: EvaluationTarget & { data: ReturnEvaluationInput }) =>
      api.post(`${evaluationPath(target)}/return`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [FOLLOW_UPS_KEY] }),
  })
}

const supervisorPath = (enrollmentId: string) =>
  `/api/training-follow-ups/enrollment/${enrollmentId}/supervisor`

/** 人事による上司評価者の個別指定 */
export function useAssignSupervisor() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ enrollmentId, data }: { enrollmentId: string; data: AssignSupervisorInput }) =>
      api.put(supervisorPath(enrollmentId), data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [FOLLOW_UPS_KEY] }),
  })
}

export function useClearSupervisorAssignment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (enrollmentId: string) => api.delete(supervisorPath(enrollmentId)),
    onSuccess: () => qc.invalidateQueries({ queryKey: [FOLLOW_UPS_KEY] }),
  })
}

export function useAddFollowUpComment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AddFollowUpCommentInput }) =>
      api.post(`/api/training-follow-ups/${id}/comments`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [FOLLOW_UPS_KEY] }),
  })
}
